import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Box, Divider, Grid, InputAdornment, Typography } from '@material-ui/core';
import Helmet from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { ReactComponent as SearchIcon } from 'assets/icons/system/search-icon.svg';
import { AccountCard } from 'components';
import { authModel } from 'models';
import { AUTH_HEADERS } from 'models/constants';
import { ROUTES } from 'routes/constants';
import { getQueryParams } from 'utils';
import { redirectToSignIn } from 'utils/helpers/urlHelpers';

import { SEARCH_THRESHOLD, filterAccounts } from './helpers';
import { SPACE_CARD_ATTRIBUTE, useVisibleItemsHeight } from './useVisibleItemsHeight';

import { AuthCard, FormLink, ScrollableList, SearchField } from '../style';


const SelectAccountPage = () => {
  const { t } = useTranslation();
  const history = useHistory();

  const [accounts, setAccounts] = useState([]);
  const [preAuthToken, setPreAuthToken] = useState();
  const [searchTerm, setSearchTerm] = useState('');

  const filteredAccounts = useMemo(() => filterAccounts(accounts, searchTerm), [accounts, searchTerm]);
  const listRef = useRef(null);
  const listMaxHeight = useVisibleItemsHeight(listRef, accounts.length, SEARCH_THRESHOLD);

  useEffect(() => {
    const preAuthToken = getQueryParams().pre_auth_token;

    if (preAuthToken) {
      setPreAuthToken(preAuthToken);
      authModel.listAccounts({ token: preAuthToken }).then(response => {
        if (response.pubtoolToken) {
          localStorage.setItem(AUTH_HEADERS.PUBTOOL_TOKEN, response.pubtoolToken);
          history.push(ROUTES.home);
        } else if (response.accounts.length === 0)
          history.push({
            pathname: ROUTES.authGoogleAccountNotFound,
            search: `?pre_auth_token=${preAuthToken}`,
          });
        else setAccounts(response.accounts);
      });
    } else redirectToSignIn();
  }, []);

  const onSelectAccount = account => {
    authModel
      .socialSignIn({
        id: account.id,
        token: preAuthToken,
      })
      .then(response => {
        if (response.pubtoolToken) {
          localStorage.setItem(AUTH_HEADERS.PUBTOOL_TOKEN, response.pubtoolToken);
          history.push(ROUTES.home);
        }
      });
  };

  if (accounts.length === 0) return null;

  return (
    <AuthCard>
      <Helmet title={t('pages.auth.selectAccountPage.tabTitle')} />
      <Grid container direction="column" spacing={6}>
        <Grid item>
          <Typography variant="h2">{t('pages.auth.selectAccountPage.title')}</Typography>
        </Grid>
        {accounts.length > SEARCH_THRESHOLD && (
          <Grid item>
            <SearchField
              variant="outlined"
              fullWidth
              size="small"
              value={searchTerm}
              placeholder={t('pages.auth.selectAccountPage.searchPlaceholder')}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              onChange={event => setSearchTerm(event.target.value)}
            />
          </Grid>
        )}
        <Grid item>
          <ScrollableList ref={listRef} maxHeight={listMaxHeight}>
            <Box display="flex" flexDirection="column" gridGap={16}>
              {filteredAccounts.map(account => (
                <div key={account.client} {...{ [SPACE_CARD_ATTRIBUTE]: true }}>
                  <AccountCard title={account.client} text={account.email} onClick={() => onSelectAccount(account)} />
                </div>
              ))}
              {filteredAccounts.length === 0 && (
                <Typography variant="body1">{t('pages.auth.selectAccountPage.noResults')}</Typography>
              )}
            </Box>
          </ScrollableList>
        </Grid>
        <Grid item>
          <Divider />
        </Grid>
        <Grid item style={{ textAlign: 'center' }}>
          <FormLink variant="text" text={t('pages.auth.common.toSignInBtn')} onClick={redirectToSignIn} />
        </Grid>
      </Grid>
    </AuthCard>
  );
};

export default SelectAccountPage;
